import { useEffect, useMemo, useRef, useState } from "react";
import {
  PrimaryLogo,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Divider,
  cn,
} from "@jem-hr/jem-ui";
import {
  Home,
  LayoutGrid,
  LayoutDashboard,
  Megaphone,
  ClipboardList,
  FileText,
  FileHeart,
  Mail,
  Radio,
  Users,
  HandCoins,
  PiggyBank,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileBarChart,
  Settings,
  X,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** If set, this item renders as an indented child under the item with the matching key. */
  parent?: string;
}

export interface SideNavbarProps {
  navItems?: NavItem[];
  activeItem?: string;
  collapsed?: boolean;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  userName?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  onCollapse?: () => void;
  onNavigate?: (key: string) => void;
  onUserClick?: () => void;
  className?: string;
}

export const defaultNavItems: NavItem[] = [
  { key: "home", label: "Home", icon: Home, href: "/" },
  { key: "recons", label: "Recons", icon: LayoutGrid, href: "/recons" },
  { key: "announcements", label: "Announcements", icon: Megaphone, href: "/announcements" },
  { key: "multimessaging", label: "Multimessaging", icon: Radio, href: "/multimessaging" },
  { key: "surveys", label: "Surveys", icon: ClipboardList, href: "/surveys" },
  { key: "my-documents", label: "My Documents", icon: FileText, href: "/my-documents" },
  { key: "beneficiary-forms", label: "Beneficiary Forms", icon: FileHeart, href: "/beneficiary-forms" },
  { key: "inbox", label: "Inbox", icon: Mail, href: "/inbox" },
  { key: "employees", label: "Employees", icon: Users, href: "/employees" },
  { key: "financial-benefits", label: "Financial Benefits", icon: HandCoins, href: "/financial-benefits" },
  { key: "savings", label: "Savings", icon: PiggyBank, href: "/savings" },
  // Time & Attendance is a grouping parent — clicking the row toggles the dropdown,
  // does not navigate. Children render the T&A pages.
  { key: "time-attendance", label: "Time & Attendance", icon: Clock, href: "#" },
  { key: "time-attendance-overview", label: "Overview", icon: LayoutDashboard, href: "/time-attendance", parent: "time-attendance" },
  { key: "reports", label: "Reports", icon: FileBarChart, href: "/reports", parent: "time-attendance" },
  { key: "site-configuration", label: "Site Configuration", icon: Settings, href: "/site-configuration", parent: "time-attendance" },
];

export function SideNavbar({
  navItems = defaultNavItems,
  activeItem = "home",
  collapsed = false,
  mobileOpen = false,
  onMobileOpenChange,
  userName = "Username",
  userInitials = "CN",
  userAvatarUrl,
  onCollapse,
  onNavigate,
  onUserClick,
  className,
}: SideNavbarProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Escape closes the drawer; focus moves into the drawer when it opens and
  // returns to the previously-focused element when it closes.
  useEffect(() => {
    if (!mobileOpen) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMobileOpenChange?.(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lastFocusedRef.current?.focus();
    };
  }, [mobileOpen, onMobileOpenChange]);

  return (
    <>
      {/* Phone-only backdrop when drawer is open */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 sm:hidden"
          onClick={() => onMobileOpenChange?.(false)}
          aria-hidden="true"
        />
      )}
      <nav
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen ? true : undefined}
        aria-label={mobileOpen ? "Main menu" : undefined}
        className={cn(
          "bg-secondary-pink-50 flex flex-col h-screen overflow-hidden transition-[width,transform] duration-200",
          // Phone: full-screen drawer from the right
          "fixed inset-y-0 right-0 z-50 w-full px-sm",
          mobileOpen ? "translate-x-0" : "translate-x-full",
          // Tablet+: in flow, allow collapsed/expanded width
          "sm:static sm:translate-x-0 sm:z-auto",
          collapsed ? "sm:w-[68px] sm:px-xs" : "sm:w-[236px] sm:px-sm",
          className
        )}
      >
      {/* Logo + toggle row */}
      <div
        className={cn(
          "flex items-center pt-sm pb-xs",
          // Phone: always justify-between (logo + close)
          "justify-between",
          // Tablet+: respect collapsed
          collapsed ? "sm:justify-center" : "sm:justify-between"
        )}
      >
        <button
          type="button"
          onClick={() => onNavigate?.("home")}
          aria-label="Go to home"
          className={cn("flex items-center", collapsed && "sm:hidden")}
        >
          <PrimaryLogo variant="bg-pink" className="h-auto w-[60px]" />
        </button>
        {/* Phone close button */}
        <Button
          ref={closeButtonRef}
          variant="ghost"
          size="icon-lg"
          onClick={() => onMobileOpenChange?.(false)}
          aria-label="Close menu"
          className="text-secondary-pink-900 hover:bg-secondary-pink-100 sm:hidden"
        >
          <X size={20} />
        </Button>
        {/* Desktop collapse toggle */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="text-secondary-pink-900 hover:bg-secondary-pink-100 hidden sm:inline-flex"
        >
          {collapsed ? <ChevronRight size={19} /> : <ChevronLeft size={19} />}
        </Button>
      </div>

      <Divider variant="secondary" />

      {/* Navigation */}
      <NavList
        navItems={navItems}
        activeItem={activeItem}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />

      {/* User */}
      <Divider variant="secondary" />
      <button
        type="button"
        onClick={onUserClick}
        className={cn(
          "flex items-center py-xs",
          collapsed ? "justify-center" : "gap-xxs"
        )}
      >
        <Avatar size="md">
          {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt={userName} />}
          <AvatarFallback size="md" className="bg-slate-400 text-primary-navy-50">{userInitials}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <>
            <span className="text-sm font-semibold leading-[20px] text-greyscale-text-caption">
              {userName}
            </span>
            <ChevronRight size={19} className="text-slate-400 ml-auto" />
          </>
        )}
      </button>
      </nav>
    </>
  );
}

/* ================================================================== */
/*  NavList — renders items, grouping children under expandable parents */
/* ================================================================== */

interface NavListProps {
  navItems: NavItem[];
  activeItem: string;
  collapsed: boolean;
  onNavigate?: (key: string) => void;
}

// A grouping parent is non-navigable — it only exists to group children.
// Marker: href = "#" (no real destination).
function isGroupingParent(item: NavItem, childrenByParent: Map<string, NavItem[]>): boolean {
  const children = childrenByParent.get(item.key);
  return Boolean(children && children.length > 0) && item.href === "#";
}

function NavList({ navItems, activeItem, collapsed, onNavigate }: NavListProps) {
  // Build parent → children map from flat nav items
  const { parents, childrenByParent } = useMemo(() => {
    const parents: NavItem[] = [];
    const childrenByParent = new Map<string, NavItem[]>();
    for (const item of navItems) {
      if (item.parent) {
        const arr = childrenByParent.get(item.parent) ?? [];
        arr.push(item);
        childrenByParent.set(item.parent, arr);
      } else {
        parents.push(item);
      }
    }
    return { parents, childrenByParent };
  }, [navItems]);

  // Flat list used when the sidebar is collapsed — drops grouping parents
  // (they have no useful click behaviour in collapsed mode) and surfaces
  // children as top-level icons instead.
  const flatCollapsedItems = useMemo(() => {
    return navItems.filter((item) => !isGroupingParent(item, childrenByParent));
  }, [navItems, childrenByParent]);

  // Parent keys that contain the active item — expand these by default.
  // Time & Attendance is always expanded so its sub-links are visible
  // (especially on mobile where it's the only parent shown).
  const defaultExpanded = useMemo(() => {
    const s = new Set<string>();
    childrenByParent.forEach((children, parentKey) => {
      if (children.some((c: NavItem) => c.key === activeItem)) s.add(parentKey);
    });
    s.add("time-attendance");
    return s;
  }, [childrenByParent, activeItem]);

  const [expanded, setExpanded] = useState<Set<string>>(defaultExpanded);

  const isExpanded = (key: string) => expanded.has(key) || defaultExpanded.has(key);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Collapsed mode: render a flat icon-only list, skipping grouping parents.
  if (collapsed) {
    return (
      <div className="flex flex-col gap-xxxs pt-xs flex-1">
        {flatCollapsedItems.map((item) => {
          const ItemIcon = item.icon;
          const isActive = activeItem === item.key;
          return (
            <a
              key={item.key}
              href={item.href}
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(item.key);
                }
              }}
              title={item.label}
              className={cn(
                "flex items-center justify-center rounded-xl text-sm font-semibold leading-18 transition-colors h-[42px]",
                isActive
                  ? "bg-secondary-pink-100 text-greyscale-text-title"
                  : "text-greyscale-text-caption hover:bg-secondary-pink-100/50",
              )}
            >
              <ItemIcon size={20} className="shrink-0" />
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-xxxs pt-xs flex-1">
      {parents.map((item) => {
        const children = childrenByParent.get(item.key);
        const hasChildren = Boolean(children && children.length > 0);
        const ItemIcon = item.icon;
        const isActive = activeItem === item.key;
        const hasActiveChild = hasChildren && children!.some((c: NavItem) => c.key === activeItem);
        const expandedForParent = hasChildren && isExpanded(item.key);

        return (
          <div
            key={item.key}
            className={cn(
              "flex-col gap-xxxs",
              item.key === "time-attendance" || item.key === "home"
                ? "flex"
                : "hidden sm:flex"
            )}
          >
            <a
              href={item.href}
              onClick={(e) => {
                if (hasChildren) {
                  // Parents with children are pure dropdown toggles — never navigate.
                  e.preventDefault();
                  toggle(item.key);
                  return;
                }
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(item.key);
                }
              }}
              aria-expanded={hasChildren ? expandedForParent : undefined}
              aria-controls={hasChildren ? `navgroup-${item.key}` : undefined}
              className={cn(
                "flex items-center gap-xxs px-xs rounded-xl text-base sm:text-sm font-semibold leading-24 sm:leading-18 transition-colors h-[42px]",
                isActive
                  ? "bg-secondary-pink-100 text-greyscale-text-title"
                  : hasActiveChild
                  ? "text-greyscale-text-title hover:bg-secondary-pink-100/50"
                  : "text-greyscale-text-caption hover:bg-secondary-pink-100/50"
              )}
            >
              <ItemIcon size={20} className="shrink-0" />
              <span className="flex-1 min-w-0 truncate whitespace-nowrap">{item.label}</span>
              {hasChildren && item.key !== "time-attendance" && (
                <ChevronDown
                  size={16}
                  className={cn(
                    "shrink-0 transition-transform",
                    expandedForParent ? "rotate-0" : "-rotate-90"
                  )}
                />
              )}
            </a>

            {hasChildren && expandedForParent && (
              <div id={`navgroup-${item.key}`} role="group" aria-label={item.label} className="flex flex-col gap-xxxs">
                {children!.map((child) => {
                  const childActive = activeItem === child.key;
                  return (
                    <a
                      key={child.key}
                      href={child.href}
                      onClick={(e) => {
                        if (onNavigate) {
                          e.preventDefault();
                          onNavigate(child.key);
                        }
                      }}
                      className={cn(
                        "flex items-center rounded-xl text-base sm:text-sm font-regular leading-24 sm:leading-18 transition-colors h-[34px] pl-[40px] pr-xs",
                        childActive
                          ? "bg-secondary-pink-100 text-greyscale-text-title font-semibold"
                          : "text-greyscale-text-caption hover:bg-secondary-pink-100/50"
                      )}
                    >
                      <span className="truncate whitespace-nowrap">{child.label}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
